import { DateTime } from 'luxon'
import { z } from 'zod'

/**
 * Date `YYYY-MM-DD` d'un paramètre de recherche (URL), transmise telle quelle à l'API. Schéma Zod v4 :
 * `hr/planning.dashboard.tsx`, encore en Zod v3, garde sa copie jusqu'à sa migration.
 */
export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

/** `isoDateSchema` et date réelle (pas de 30 février) : ce qu'exige un validateur de date côté API. */
export const realIsoDateSchema = isoDateSchema.refine((value) => DateTime.fromISO(value).isValid)

/**
 * Date ISO `YYYY-MM-DD` au format français `dd/MM/yyyy`. Copies restantes, à faire converger :
 * `formatDayLong` (store-dashboard/filters/date-shortcuts.ts, fichier volontairement chargeable par
 * `node --test` sans alias) et `frenchShortDate` (previsions/-previsions-format.ts, qui renvoie
 * l'ISO brut sur une entrée invalide là où Luxon rend « Invalid DateTime »). Conversions
 * Date ↔ `YYYY-MM-DD` locales : `toIsoDate` / `parseLocalDate` (`~/utils/dates/local-date`).
 */
export const frenchDateOfIso = (iso: string): string => DateTime.fromISO(iso).toFormat('dd/MM/yyyy')

export const frenchDayMonthOfIso = (iso: string): string => DateTime.fromISO(iso).toFormat('dd/MM')

/** « lundi 02/11 » : jour de la semaine, jour et mois. */
export const frenchWeekdayDayMonthOfIso = (iso: string): string =>
  DateTime.fromISO(iso).setLocale('fr').toFormat('cccc dd/MM')

export const frenchLongDateOfIso = (iso: string): string =>
  DateTime.fromISO(iso).setLocale('fr').toFormat('d MMMM yyyy')

/** Jour, à Paris, d'un horodatage ISO (avec fuseau) : « 15/11/2026 », quel que soit le fuseau de l'ISO. */
export const frenchParisDayOfInstant = (iso: string): string =>
  DateTime.fromISO(iso).setZone('Europe/Paris').toFormat('dd/MM/yyyy')

export const frenchDateTime = (date: Date): string =>
  DateTime.fromJSDate(date).setZone('Europe/Paris').toFormat("dd/MM/yyyy 'à' HH:mm")

/** Jour, mois et heure, à Paris, d'un horodatage ISO : « 18/10 à 09:42 ». */
export const frenchParisDayMonthTimeOfInstant = (iso: string): string =>
  DateTime.fromISO(iso).setZone('Europe/Paris').toFormat("dd/MM 'à' HH:mm")
